import React, { useState } from "react";
import { Link } from "react-router-dom";
import useSWR from "swr";
import { api, errMessage } from "../api";
import {
  Rule,
  Footer,
  Loading,
  Nav,
  Verse,
  ErrorNote,
  useBodyClass,
  useDocumentTitle,
} from "../components/Chrome";
import Newsletter from "../components/Newsletter";
import { craftPlate } from "../lib/crafts";

const fetcher = (url) => api.get(url).then((r) => r.data);
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

function InterestForm({ circle, onDone }) {
  const [form, setForm] = useState({ name: "", email: "", note: "" });
  const [errors, setErrors] = useState({});
  const [sending, setSending] = useState(false);
  const [failure, setFailure] = useState("");

  const set = (k) => (e) => {
    setForm((f) => ({ ...f, [k]: e.target.value }));
    setErrors((x) => ({ ...x, [k]: undefined }));
  };

  const submit = async (e) => {
    e.preventDefault();
    setFailure("");
    const errs = {};
    if (!form.name.trim()) errs.name = "This needs an answer.";
    if (!EMAIL.test(form.email.trim()))
      errs.email = "That email doesn't look quite right.";
    setErrors(errs);
    if (Object.keys(errs).length) return;

    setSending(true);
    try {
      const { data } = await api.post(`/circles/${circle.id}/interest`, {
        name: form.name.trim(),
        email: form.email.trim(),
        note: form.note.trim(),
      });
      onDone(data.status);
    } catch (err) {
      setFailure(errMessage(err, "We couldn't take that just now. Try again?"));
    } finally {
      setSending(false);
    }
  };

  return (
    <form
      onSubmit={submit}
      noValidate
      style={{ marginTop: "1.4rem" }}
      data-testid={`circle-interest-form-${circle.id}`}
    >
      <div className="hp" aria-hidden="true">
        <label htmlFor={`hp-${circle.id}`}>Leave this empty</label>
        <input id={`hp-${circle.id}`} tabIndex={-1} autoComplete="off" />
      </div>
      <div className="row">
        <div className="field">
          <label htmlFor={`ci-name-${circle.id}`}>Your name</label>
          <input
            id={`ci-name-${circle.id}`}
            value={form.name}
            onChange={set("name")}
            autoComplete="name"
            aria-invalid={errors.name ? "true" : "false"}
            data-testid="circles-interest-name-input"
          />
          {errors.name ? (
            <span className="err">
              <span className="lab">Note</span>
              {errors.name}
            </span>
          ) : null}
        </div>
        <div className="field">
          <label htmlFor={`ci-email-${circle.id}`}>Email</label>
          <input
            id={`ci-email-${circle.id}`}
            type="email"
            inputMode="email"
            value={form.email}
            onChange={set("email")}
            autoComplete="email"
            aria-invalid={errors.email ? "true" : "false"}
            data-testid="circles-interest-email-input"
          />
          {errors.email ? (
            <span className="err">
              <span className="lab">Note</span>
              {errors.email}
            </span>
          ) : null}
        </div>
      </div>
      <div className="field">
        <label htmlFor={`ci-note-${circle.id}`}>
          What you make, and how long you have been at it{" "}
          <span className="opt">&mdash; optional</span>
        </label>
        <textarea
          id={`ci-note-${circle.id}`}
          rows={2}
          value={form.note}
          onChange={set("note")}
          data-testid="circles-interest-note-textarea"
        />
      </div>
      <button
        type="submit"
        className="act"
        disabled={sending}
        data-testid="circles-interest-submit-button"
      >
        {sending ? "Sending\u2026" : "Sign me up \u2192"}
      </button>
      {failure ? <div className="form-error">{failure}</div> : null}
    </form>
  );
}

function CircleRow({ c }) {
  const [open, setOpen] = useState(false);
  const [done, setDone] = useState(null);
  const plate = craftPlate(c.medium, c.title);
  const full = c.is_full || c.status === "full";

  return (
    <article
      className={`circle${plate ? "" : " plain"}`}
      data-testid={`circle-row-${c.id}`}
    >
      {plate ? (
        <img
          className="cplate"
          src={plate.src}
          alt=""
          loading="lazy"
          data-testid={`circle-plate-${c.id}`}
        />
      ) : null}
      <div className="cbody">
        <div className="ctop">
          <h3>{c.title || `${c.medium} circle`}</h3>
          {full ? (
            <span className="kind" data-testid={`circle-full-${c.id}`}>
              Full &mdash; names welcome
            </span>
          ) : null}
        </div>
        {c.description ? <p className="desc">{c.description}</p> : null}

        {done ? (
          <div className="notice ok" data-testid={`circle-interest-done-${c.id}`}>
            {done === "already_registered"
              ? "Your name is already down for this one. We'll be in touch."
              : "Your name is down. We'll write to you with the invitation and where the conversation happens."}
          </div>
        ) : open ? (
          <InterestForm circle={c} onDone={setDone} />
        ) : (
          <div className="actions">
            <button
              type="button"
              className="act"
              onClick={() => setOpen(true)}
              data-testid={`circle-interest-open-${c.id}`}
            >
              Sign me up &rarr;
            </button>
          </div>
        )}
      </div>
    </article>
  );
}

export default function Circles() {
  useBodyClass("inner");
  useDocumentTitle(
    "Circles",
    "Sign up for a Sch\u0113ma circle: writing, painting, music, dance, craft or culinary.",
  );

  const { data, error, isLoading, mutate } = useSWR("/circles", fetcher);
  const circles = data?.circles || [];

  return (
    <>
      <Nav />
      <header className="page-head" data-testid="circles-page-head">
        <div className="prose">
          <p className="eyebrow sc">
            <span className="num">&middot;</span>
            <span>Sign up</span>
          </p>
          <h1>Circles</h1>
          <p className="standfirst">
            Writers with writers, painters with painters. Pick the craft that is
            yours, sign up, and we&rsquo;ll write to you when that circle meets.
            Nothing to pay, and no portfolio to send.
          </p>
        </div>
      </header>

      <section data-testid="circles-list">
        <div className="prose">
          {isLoading ? (
            <Loading label="Looking" testid="circles-loading" />
          ) : error ? (
            <ErrorNote
              message="We couldn't load the circles just now."
              onRetry={() => mutate()}
              testid="circles-error"
            />
          ) : circles.length === 0 ? (
            <div className="empty" data-testid="circles-empty-state">
              <h3 style={{ marginBottom: ".8rem" }}>No circles open yet.</h3>
              <p>
                Sign-ups will appear here. In the meantime, tell us what you
                make and we will start a list for it.
              </p>
              <p style={{ marginTop: "1.4rem" }}>
                <Link to="/gatherings" className="linkish">
                  Come to a one-off &rarr;
                </Link>
              </p>
            </div>
          ) : (
            circles.map((c) => <CircleRow key={c.id} c={c} />)
          )}
        </div>
      </section>

      <Rule />

      <section data-testid="circles-why">
        <div className="prose">
          <p className="eyebrow sc">
            <span className="num">&middot;</span>
            <span>What happens next</span>
          </p>
          <h2>You sign up here. The talking happens elsewhere.</h2>
          <p>
            Signing up puts your name and your craft in front of us, and
            that&rsquo;s all it does. When your circle has enough people, we
            write to you with the first date and where the conversation lives.
          </p>
          <p>
            Circles are same-medium on purpose: nobody can tell you why the
            second act is limp unless they have had to solve that problem
            themselves. Cross-pollination is a pleasure, and we keep it for the
            showings and the one-offs.
          </p>
          <p>
            Don&rsquo;t see your craft? Say so on the{" "}
            <Link to="/join">get in touch</Link> form and we will start a list
            for it.
          </p>
        </div>
      </section>

      <Rule />

      <section data-testid="circles-newsletter">
        <div className="prose">
          <Newsletter
            heading="Tell me when a circle opens."
            promise="One email when there's a new circle or a date worth turning up to."
            source="circles"
            testidPrefix="circles-newsletter"
          />
        </div>
      </section>

      <Verse />
      <Footer />
    </>
  );
}
